import React, { type PropsWithChildren } from "react";

import useRedux from "~redux/hooks";
import {
    type VariationComponent,
    type PreviewNotes,
    ModuleSource,
    ZoneColumnCombo,
} from "~types/types";
import { getProvenance } from "~utils/moduleProvenance";

import { InfoCard, InfoCardType } from "./InfoCard";
import styles from "./InfoMode.module.css";

const EDIT_ZONE_NAMES = {
    [ZoneColumnCombo.gridRightRail]: "Right Rail (of righty)",
    [ZoneColumnCombo.leftContentWell]: "Left Content Well (of righty)",
    [ZoneColumnCombo.fullWidthContentWell]: "Full Width Content Well",
    [ZoneColumnCombo.full]: "Unsupported",
    [ZoneColumnCombo.leftRail]: "Unsupported",
    [ZoneColumnCombo.switchContent]: "Unsupported",
    [ZoneColumnCombo.rightyContent]: "Unsupported",
    [ZoneColumnCombo.leftyContent]: "Unsupported",
    [ZoneColumnCombo.rightRail]: "Unsupported",
    [ZoneColumnCombo.unsupported]: "Unsupported",
};

interface InfoModeProps {
    source: VariationComponent;
    withVariantHeader?: boolean;
    supportedRegions?: ZoneColumnCombo[];
    previewNotes?: PreviewNotes;
}

export function moduleIsInRightRail(source: VariationComponent) {
    return source.renderingLocation === ZoneColumnCombo.gridRightRail;
}

export function moduleIsMissingItsShadow(source: VariationComponent): boolean {
    return moduleIsInRightRail(source) && Boolean(source.isMissingShadow);
}

export function moduleIsValidShadow(source: VariationComponent): boolean {
    const moduleProvenance = getProvenance(source);

    return moduleProvenance === ModuleSource.Shadow && !source.isMissingShadow;
}

export function moduleIsShadowWarning(source: VariationComponent): boolean {
    return Boolean(source.isShadowWarning);
}

export function moduleHasMiniNav(source: VariationComponent): boolean {
    return (
        Boolean(source.modules?.length) &&
        Boolean(
            source.modules.filter(
                ({ nestedVariation }) => nestedVariation === "navigation"
            ).length
        )
    );
}

export const InfoMode: React.FC<PropsWithChildren<InfoModeProps>> = (props) => {
    const {
        source,
        withVariantHeader = false,
        supportedRegions = [],
        previewNotes = {},
    } = props;

    const { id, variation, renderingLocation, type, componentName } = source;
    const { useAppSelector } = useRedux();
    const { pageId } = useAppSelector((state) => state.pageData);

    const moduleProvenance = getProvenance(source);

    if (moduleIsShadowWarning(source)) {
        return (
            <div className={styles.InfoMode} data-provenance={moduleProvenance}>
                <div className={styles.container}>
                    <div className={styles.cards}>
                        <InfoCard
                            title="Shadow Module Warning"
                            emoji="📵"
                            expanded
                            cardType={InfoCardType.error}
                        >
                            <div className={styles.warning}>
                                This module is curated into the right rail and
                                it does not have a shadow module, so{" "}
                                <strong>
                                    it will not appear on XS desktop, tablet nor
                                    mobile web breakpoints
                                </strong>
                                . Please reach out in <em>#dev-audience</em> if
                                you need help adding a shadow module.
                            </div>
                        </InfoCard>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.InfoMode} data-provenance={moduleProvenance}>
            <div className={styles.container}>
                <div className={styles.cards}>
                    {moduleIsMissingItsShadow(source) && (
                        <InfoCard
                            title="Shadow Module Warning"
                            emoji="📵"
                            expanded
                            cardType={InfoCardType.error}
                        >
                            <div className={styles.warning}>
                                This module is curated into the Right Rail, it{" "}
                                <strong>requires a shadow module</strong> to
                                render on lower breakpoints. It will not appear
                                below small desktop. Please reach out in{" "}
                                <em>#dev-audience</em> if you need help adding a
                                shadow module.
                            </div>
                        </InfoCard>
                    )}

                    {moduleHasMiniNav(source) && !withVariantHeader && (
                        <InfoCard
                            title="Mini-Nav Will Not Render!"
                            emoji="📵"
                            expanded
                            cardType={InfoCardType.error}
                        >
                            <div className={styles.warning}>
                                You have a mini-nav curated onto this module,
                                but this module does not support it. Please try
                                a different variation.
                            </div>
                        </InfoCard>
                    )}

                    {Boolean(previewNotes.curationName) && (
                        <InfoCard
                            title={
                                previewNotes?.curationName || "Curation Info"
                            }
                            previewNotes={previewNotes}
                            emoji="🗒️"
                            expanded
                        />
                    )}

                    {!moduleIsMissingItsShadow(source) &&
                        moduleIsInRightRail(source) &&
                        !moduleIsValidShadow(source) && (
                            <InfoCard
                                title="Module has valid Shadow"
                                emoji="👍"
                                message="This module has a valid shadow module, it will render properly on ALL breakpoints."
                                cardType={InfoCardType.valid}
                            />
                        )}

                    {moduleIsValidShadow(source) && (
                        <InfoCard
                            title="Valid Shadow Module"
                            emoji="👍"
                            expanded
                            cardType={InfoCardType.valid}
                        >
                            <div>
                                This is a shadow module for{" "}
                                <em>{source.duplicateOf}</em>. It will render on
                                extra-small desktop, tablet and mobile web
                                breakpoints. The original module will render in
                                the right rail on small and large desktop
                                breakpoints.
                            </div>
                        </InfoCard>
                    )}

                    <InfoCard
                        title="Module Features"
                        emoji="⚙️"
                        expanded={false}
                    >
                        <div className="p-[6px]">
                            <div>
                                Title:{" "}
                                <em>
                                    {withVariantHeader
                                        ? "supported"
                                        : "not-supported"}
                                </em>
                            </div>
                            <div>
                                Description:{" "}
                                <em>
                                    {withVariantHeader
                                        ? "supported"
                                        : "not-supported"}
                                </em>
                            </div>
                            <div>
                                Module Mini-Nav:{" "}
                                <em>
                                    <a
                                        href="https://docs.google.com/document/d/1XVAaeEBxk8ifuhKiUqNn_jvpwPbTet681IRnLv4MLRQ/edit?tab=t.0#heading=h.s94uv6uqk6ci"
                                        target="_blank"
                                        className="underline"
                                    >
                                        {withVariantHeader
                                            ? `supported via nested module`
                                            : "not-supported"}
                                    </a>
                                </em>
                            </div>
                            <br />
                            <div>
                                This module will work in the following columns:{" "}
                                <br />
                                {supportedRegions.map((region, index) => (
                                    <em key={index}>
                                        &nbsp;&bull;&nbsp;{" "}
                                        {EDIT_ZONE_NAMES[region] || region}
                                        <br />
                                    </em>
                                ))}
                            </div>
                        </div>
                    </InfoCard>

                    <InfoCard
                        title="Troubleshooting"
                        emoji="🆘"
                        expanded={false}
                        message="If you're having trouble with this module, take a screen shot and reach out in #help-dev."
                    >
                        <div className="p-[6px]">
                            <div>
                                Module ID: <em>{id}</em>
                            </div>
                            <div>
                                Page ID: <em>{pageId}</em>
                            </div>

                            <div>
                                Type: <em>{type}</em>
                            </div>
                            <div>
                                Variation: <em>{variation || "default"}</em>
                            </div>
                            <div>
                                Rendering Location: <em>{renderingLocation}</em>
                            </div>
                            <div>
                                Component Name: <em>&lt;{componentName}&gt;</em>
                            </div>
                        </div>
                    </InfoCard>
                </div>
            </div>
        </div>
    );
};
